/**
 * ForEmployeeRelationsPage — the public /for/employee-relations archetype
 * landing page.
 *
 * Ported verbatim from the locked design
 * design/mockups/V1_Landing_For_Employee_Relations_v1.html (Template 2
 * instance, source V1_Archetype_Landing_Pages.md Page 5). Replaces the noindex
 * marketing scaffold for /for/employee-relations with real, indexable content.
 *
 * The app shell's <TopNav> renders the nav; this page is content + footer
 * (everything below the mockup's <nav class="marketing-nav">). Scoped <style>
 * block aliases the mockup's colors onto locked --ppr-* tokens; replicates the
 * locked pattern from PricingPage.jsx.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const EMPREL_DESCRIPTION =
  "PeoplePrints gives employee relations specialists a tactical read on both parties before every mediation — generated from data you already have, no assessment required. From $349/month.";

const FAQS = [
  {
    q: "Can PeoplePrints help me mediate a conflict between two employees?",
    a: "Yes — it is built for exactly this. The Conflict Mediation Blueprint generates a Predisposition Profile for each party and one mediation sequence: what to surface to whom, in what order, and where the parties' convergence point is. It is a first-class V1 feature; no other major HR-adjacent tool has a conflict-mediation-specific surface.",
  },
  {
    q: "Do the employees in conflict have to take an assessment?",
    a: "No. PeoplePrints generates each Profile from minimal common data points already on file. Neither party has to participate — which matters, because people already in conflict will not sit for an assessment to help you mediate.",
  },
  {
    q: "Is PeoplePrints a case-management or investigation system?",
    a: "No. PeoplePrints is a pre-interaction tool — it prepares you for the mediation conversation with a read on both parties. It does not manage ER cases, conduct investigations, or store grievance documentation. It works alongside whatever case system you already use.",
  },
  {
    q: "How is this different from reading the room in real time?",
    a: "Reading the room in real time means you are forming the read and running the mediation at the same time. PeoplePrints does the read before the room, so you walk in able to focus entirely on the conversation — and the Blueprint flags each party's convergence point so the agreement anchors to something durable.",
  },
  {
    q: "What does it cost?",
    a: "The HR Director tier, which includes the Conflict Mediation Blueprint, is $349/month for up to 200 Profiles.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function ForEmployeeRelationsPage() {
  usePageMeta({
    title:
      "Read Both Parties Before the Mediation — PeoplePrints for Employee Relations",
    description: EMPREL_DESCRIPTION,
    canonicalPath: "/for/employee-relations",
  });

  return (
    <div className="ppr-emprel-root">
      <style>{EMPREL_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-emprel-hero">
        <div className="ppr-emprel-hero-inner">
          <p className="ppr-emprel-hero-eyebrow">
            For employee relations specialists
          </p>
          <h1 className="ppr-emprel-hero-h1">
            Walk into the mediation with both parties already read.
          </h1>
          <p className="ppr-emprel-hero-sub">
            Mediation is your job, and it lives or dies on the read. You sit
            between two people who have stopped hearing each other — and you
            can't ask either of them to take an assessment to help you.
            PeoplePrints gives you a tactical read on both parties before the
            room, generated from data you already have, so the mediation
            produces an agreement that holds instead of a truce that breaks.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-emprel-block ppr-emprel-compact">
        <div className="ppr-emprel-block-inner">
          <div className="ppr-emprel-definition">
            <p className="ppr-emprel-def-label">
              What PeoplePrints does for employee relations specialists
            </p>
            <p className="ppr-emprel-def-text">
              PeoplePrints is a pre-interaction intelligence tool for employee
              relations specialists. It generates a{" "}
              <strong>Predisposition Profile</strong> for each party in a
              workplace conflict from minimal common data points — no
              assessment required — and converts the two Profiles into a
              Conflict Mediation Blueprint: one mediation sequence that tells
              the specialist what to surface to each party, in what order, and
              where the convergence point between them is. It supports
              mediation preparation; it is not a case-management or
              investigation system.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE PAIN ── */}
      <section className="ppr-emprel-block ppr-emprel-compact">
        <div className="ppr-emprel-block-inner">
          <p className="ppr-emprel-block-eyebrow">Sound familiar?</p>
          <h2 className="ppr-emprel-block-h2">
            The one thing you most need is the one thing no tool gives you.
          </h2>
          <p className="ppr-emprel-pain-quote">
            Two senior contributors have stopped speaking. A grievance is
            sitting on your desk. A team is fracturing and the manager who
            flagged it can't tell you why. You mediate for a living — and the
            one thing you most need, a real read on both parties going in, is
            the one thing no tool gives you. You can't run an assessment on two
            people in conflict. So you walk in and read the room in real time,
            and the "find common ground" approach produces a truce that breaks
            the next time either party feels disrespected.
          </p>
        </div>
      </section>

      {/* ── BLOCK 5 — THE SHIFT ── */}
      <section className="ppr-emprel-block ppr-emprel-compact ppr-emprel-tinted">
        <div className="ppr-emprel-block-inner">
          <p className="ppr-emprel-block-eyebrow">The shift</p>
          <h2 className="ppr-emprel-block-h2">
            The read is done before the room.
          </h2>
          <p className="ppr-emprel-block-body">
            With PeoplePrints, the read is done before the room. The Conflict
            Mediation Blueprint generates a Predisposition Profile for each
            party and one mediation sequence — what each party needs to feel
            heard, what each needs to feel respected, what each will agree to
            and where each will dig in. It surfaces the convergence point both
            Profiles share, so you anchor the agreement to something durable
            instead of to a fragile handshake.
          </p>
        </div>
      </section>

      {/* ── BLOCK 6 — WHAT YOU GET (In-This-Blueprint card grammar) ── */}
      <section className="ppr-emprel-block ppr-emprel-compact">
        <div className="ppr-emprel-block-inner">
          <h2 className="ppr-emprel-block-h2">
            The Blueprints that matter to an ER specialist.
          </h2>
          <div className="ppr-emprel-itb-card">
            <div className="ppr-emprel-itb-stripe"></div>
            <div className="ppr-emprel-itb-inner">
              <p className="ppr-emprel-itb-label">What you get</p>
              <ol className="ppr-emprel-itb-list">
                <li>
                  <strong>A Predisposition Profile for each party</strong> —
                  generated from minimal common data points, no assessment, no
                  participation required from people already in conflict.
                </li>
                <li>
                  <strong>The Conflict Mediation Blueprint</strong> — two
                  Profiles, one mediation sequence: what to surface to whom, in
                  what order, where the convergence point is. No other HR tool
                  has a conflict-mediation-specific surface.
                </li>
                <li>
                  <strong>The full Blueprint library</strong> — performance
                  review, PIP, stay interview, skip-level, and exit interview
                  Blueprints for the rest of the ER surface.
                </li>
                <li>
                  <strong>Tenant admin controls + audit log</strong> — a full
                  activity trail for every Profile and Blueprint.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 7 — HOW IT WORKS ── */}
      <section className="ppr-emprel-block ppr-emprel-compact ppr-emprel-tinted">
        <div className="ppr-emprel-block-inner">
          <p className="ppr-emprel-block-eyebrow">How it works</p>
          <h2 className="ppr-emprel-block-h2">
            Minimal data in. Two Profiles. One mediation sequence.
          </h2>
          <div className="ppr-emprel-step-list">
            <div className="ppr-emprel-step-row">
              <div className="ppr-emprel-step-num">1</div>
              <p className="ppr-emprel-step-text">
                <strong>Enter the common data points for both parties.</strong>
              </p>
            </div>
            <div className="ppr-emprel-step-row">
              <div className="ppr-emprel-step-num">2</div>
              <p className="ppr-emprel-step-text">
                <strong>
                  The engine generates a Predisposition Profile for each.
                </strong>
              </p>
            </div>
            <div className="ppr-emprel-step-row">
              <div className="ppr-emprel-step-num">3</div>
              <p className="ppr-emprel-step-text">
                <strong>Open the Conflict Mediation Blueprint</strong> — and
                walk into the mediation with both reads done.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 8 — PRICING POINTER ── */}
      <section className="ppr-emprel-block ppr-emprel-compact">
        <div className="ppr-emprel-block-inner">
          <div className="ppr-emprel-price-pointer">
            <p className="ppr-emprel-pp-tier">HR Director — $349 / month</p>
            <p className="ppr-emprel-pp-body">
              The Conflict Mediation Blueprint and the full ER Blueprint
              library live in the HR Director tier: 200 Profiles per month,
              every Blueprint, the Fairness Audit, tenant admin controls and
              audit log — <strong>$349/month</strong>. If your ER function
              spans multiple specialists, talk to us about Enterprise.
            </p>
          </div>
          <p className="ppr-emprel-xlink">
            Own the wider HR surface too? See{" "}
            <Link to="/for/hr-directors">
              PeoplePrints for HR Directors →
            </Link>
          </p>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-emprel-block ppr-emprel-compact ppr-emprel-tinted">
        <div className="ppr-emprel-block-inner">
          <p className="ppr-emprel-block-eyebrow">FAQ</p>
          <h2 className="ppr-emprel-block-h2">
            Questions employee relations specialists ask.
          </h2>
          <div className="ppr-emprel-faq-list">
            {FAQS.map((f, i) => (
              <details
                className="ppr-emprel-faq-item"
                key={f.q}
                open={i === 0}
              >
                <summary className="ppr-emprel-faq-q">{f.q}</summary>
                <p className="ppr-emprel-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-emprel-cred-strip">
        <div className="ppr-emprel-cred-strip-inner">
          <span className="ppr-emprel-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          Founder Russ Cersosimo brings three decades of applied behavioral
          strategy and continuing-education curricula accredited for lawyers
          and physicians. The full methodology is at{" "}
          <Link to="/methodology">/methodology</Link>; the founder bio is at{" "}
          <Link to="/about">/about</Link>.
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-emprel-cta-band">
        <div className="ppr-emprel-cta-band-inner">
          <h2 className="ppr-emprel-cta-band-h">
            Walk into the mediation with both parties already read.
          </h2>
          <div className="ppr-emprel-cta-actions">
            <Link
              to="/demo"
              className="ppr-emprel-cta-btn ppr-emprel-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/demo"
              className="ppr-emprel-cta-btn ppr-emprel-cta-secondary"
            >
              Book a 30-minute demo
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer — clean A6 nav ── */}
      <footer className="ppr-emprel-footer">
        <div className="ppr-emprel-footer-inner">
          <div className="ppr-emprel-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-emprel-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-emprel-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const EMPREL_CSS = `
.ppr-emprel-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design (same flag as PricingPage / the C1 homepage rebuild). */
  --c-gold: #C99A4B;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-emprel-root * { box-sizing: border-box; }

.ppr-emprel-block { padding: 64px 32px; }
.ppr-emprel-block.ppr-emprel-compact { padding: 44px 32px; }
.ppr-emprel-block.ppr-emprel-tinted { background: var(--c-pale-soft); }
.ppr-emprel-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-emprel-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-emprel-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-emprel-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-emprel-block-body strong { color: var(--c-navy); }

.ppr-emprel-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-emprel-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-emprel-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-emprel-hero-h1 { font-family: var(--f-display); font-size: 40px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-emprel-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-emprel-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-emprel-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-emprel-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-emprel-def-text strong { color: var(--c-navy); }

.ppr-emprel-pain-quote { font-family: var(--f-body); font-size: 17px; font-style: italic; line-height: 1.6; color: var(--c-text); border-left: 4px solid var(--c-coral); background: var(--c-coral-very-soft); padding: 20px 24px; margin: 0; }

.ppr-emprel-itb-card { background: var(--c-surface); border: 3px solid var(--c-navy); margin: 6px 0 0; }
.ppr-emprel-itb-stripe { height: 8px; background: var(--c-coral); }
.ppr-emprel-itb-inner { padding: 24px 28px; }
.ppr-emprel-itb-label { font-family: var(--f-display); font-size: 19px; color: var(--c-navy); text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 14px; }
.ppr-emprel-itb-list { margin: 0; padding: 0; list-style: none; counter-reset: itb; }
.ppr-emprel-itb-list li { position: relative; padding: 11px 0 11px 38px; font-family: var(--f-body); font-size: 15.5px; line-height: 1.55; border-bottom: 1px solid var(--c-border); counter-increment: itb; }
.ppr-emprel-itb-list li:last-child { border-bottom: none; padding-bottom: 0; }
.ppr-emprel-itb-list li::before { content: counter(itb); position: absolute; left: 0; top: 10px; width: 23px; height: 23px; background: var(--c-navy); color: #FFF; border-radius: 50%; font-family: var(--f-display); font-size: 11px; display: flex; align-items: center; justify-content: center; }
.ppr-emprel-itb-list strong { color: var(--c-navy); }

.ppr-emprel-step-list { margin: 20px 0 0; }
.ppr-emprel-step-row { display: grid; grid-template-columns: 40px 1fr; gap: 18px; padding: 15px 0; border-bottom: 1px solid var(--c-border); }
.ppr-emprel-step-row:last-child { border-bottom: none; }
.ppr-emprel-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--c-navy); color: #FFF; font-family: var(--f-display); font-size: 13px; display: flex; align-items: center; justify-content: center; }
.ppr-emprel-step-text { font-family: var(--f-body); font-size: 15.5px; line-height: 1.6; color: var(--c-text); margin: 4px 0 0; }
.ppr-emprel-step-text strong { color: var(--c-navy); }

.ppr-emprel-price-pointer { border: 2px solid var(--c-navy); border-top: 8px solid var(--c-coral); background: var(--c-surface); padding: 26px 30px; }
.ppr-emprel-pp-tier { font-family: var(--f-display); font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 8px; }
.ppr-emprel-pp-body { font-family: var(--f-body); font-size: 16px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-emprel-pp-body strong { color: var(--c-navy); }
.ppr-emprel-xlink { font-family: var(--f-ui); font-size: 14px; color: var(--c-text-2); margin: 18px 0 0; }
.ppr-emprel-xlink a { color: var(--c-coral-dark); font-weight: 600; text-decoration: none; }

.ppr-emprel-faq-list { margin: 20px 0 0; }
.ppr-emprel-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-emprel-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; cursor: pointer; list-style: none; }
.ppr-emprel-faq-q::-webkit-details-marker { display: none; }
.ppr-emprel-faq-item[open] .ppr-emprel-faq-q { margin-bottom: 8px; }
.ppr-emprel-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-emprel-faq-a strong { color: var(--c-navy); }

.ppr-emprel-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-emprel-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-emprel-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-emprel-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-emprel-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-emprel-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-emprel-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-emprel-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-emprel-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-emprel-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-emprel-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-emprel-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-emprel-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-emprel-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-emprel-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-emprel-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-emprel-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-emprel-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-emprel-hero-h1 { font-size: 30px; }
  .ppr-emprel-block-h2 { font-size: 22px; }
}
`;
